/**
 * @file Google's map, loaded on demand, inside the footer map block (blocks/_footer-map.liquid).
 *
 * Nothing is requested from Google until a visitor presses the button: the iframe is created on
 * click and removed again on close, so a visitor who never taps never contacts Google. The same
 * approach as assets/ig-embed.js, without a dialog: the map opens in place, so focus is never
 * trapped and the page behind it never locks.
 *
 * Registers <bk-footer-map>:
 *   <bk-footer-map data-map-src="https://www.google.com/maps?q=…&output=embed"
 *     data-frame-title="…">
 *     [data-footer-map-loading] [data-footer-map-frame] [data-footer-map-close]
 *     [data-footer-map-open]
 *   </bk-footer-map>
 *
 * Every visible string is server-rendered and translated; this module only shows and hides them.
 */

/** Only Google's own map embed may be framed, whatever ends up in the attribute. */
const ALLOWED_HOST = 'www.google.com';

/**
 * How long the loading line may stay before it gives up on its own. Google's embed fires `load`
 * when it is ready, but a blocked, throttled or offline embed may never fire it at all, and a
 * loading line that never leaves is worse than none.
 */
const LOADING_TIMEOUT = 8000;

/**
 * The map embed URL of an element, when it is one.
 * @param {string} src - Value of data-map-src
 * @returns {string} The URL, or '' when it is not a Google map embed
 */
export function mapEmbedUrl(src) {
  if (typeof src !== 'string' || src.trim() === '') return '';

  /** @type {URL} */
  let parsed;
  try {
    parsed = new URL(src.trim());
  } catch {
    return '';
  }
  if (parsed.protocol !== 'https:' || parsed.hostname.toLowerCase() !== ALLOWED_HOST) return '';
  if (parsed.username || parsed.password || parsed.port) return '';
  if (!parsed.pathname.startsWith('/maps')) return '';

  return parsed.toString();
}

/**
 * Google map iframe. Not lazy: it is created only when it is asked for.
 * @param {string} src - The map embed URL
 * @param {string} title - Accessible name of the frame
 * @returns {HTMLIFrameElement | null} Null when the URL is not a Google map embed
 */
export function createMapFrame(src, title) {
  const url = mapEmbedUrl(src);
  if (!url) return null;

  const frame = document.createElement('iframe');
  frame.className = 'footer-map__iframe';
  frame.title = title;
  frame.loading = 'lazy';
  frame.referrerPolicy = 'no-referrer-when-downgrade';
  frame.allowFullscreen = true;
  frame.src = url;
  return frame;
}

class BkFooterMap extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #loadTimer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#openButton?.addEventListener('click', () => this.open(), { signal });
    this.#closeButton?.addEventListener('click', () => this.close(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#clearFrame();
  }

  /** @returns {HTMLElement | null} */
  get #openButton() {
    return this.querySelector('[data-footer-map-open]');
  }

  /** @returns {HTMLElement | null} */
  get #closeButton() {
    return this.querySelector('[data-footer-map-close]');
  }

  /** @returns {HTMLElement | null} */
  get #host() {
    return this.querySelector('[data-footer-map-frame]');
  }

  /** @returns {HTMLElement | null} */
  get #loading() {
    return this.querySelector('[data-footer-map-loading]');
  }

  /** True while Google's map is on the page. */
  get isOpen() {
    return Boolean(this.#host?.firstElementChild);
  }

  /**
   * Build the map and show it. A src that is not a Google map embed opens nothing: the outbound
   * link next to the button stays the way out.
   */
  open() {
    if (this.isOpen) return;

    const host = this.#host;
    const frame = createMapFrame(this.dataset.mapSrc ?? '', this.dataset.frameTitle ?? '');
    if (!host || !frame) return;

    const loading = this.#loading;
    if (loading) loading.hidden = false;

    // Whatever happens first — the map loads, it errors, or the safety timeout runs out — takes the
    // loading line away and records why, so a visitor is never left reading "loading" over a map
    // that is already there, or over one that is never coming.
    const settle = (reason) => {
      if (this.#loadTimer) {
        clearTimeout(this.#loadTimer);
        this.#loadTimer = null;
      }
      if (loading) loading.hidden = true;
      this.dataset.mapLoaded = reason;
    };
    frame.addEventListener('load', () => settle('load'), { once: true });
    frame.addEventListener('error', () => settle('error'), { once: true });
    this.#loadTimer = setTimeout(() => settle('timeout'), LOADING_TIMEOUT);

    host.replaceChildren(frame);
    this.toggleAttribute('data-open', true);

    const openButton = this.#openButton;
    if (openButton) {
      openButton.setAttribute('aria-expanded', 'true');
      openButton.hidden = true;
    }

    const closeButton = this.#closeButton;
    if (closeButton) {
      closeButton.hidden = false;
      // Focus moves to the control that undoes what was just done; nothing is trapped.
      closeButton.focus();
    }
  }

  /** Remove the iframe, which also stops anything it was loading. */
  close() {
    if (!this.isOpen) return;

    this.#clearFrame();

    const closeButton = this.#closeButton;
    if (closeButton) closeButton.hidden = true;

    const openButton = this.#openButton;
    if (openButton) {
      openButton.hidden = false;
      openButton.setAttribute('aria-expanded', 'false');
      openButton.focus();
    }
  }

  #clearFrame() {
    if (this.#loadTimer) {
      clearTimeout(this.#loadTimer);
      this.#loadTimer = null;
    }
    this.#host?.replaceChildren();
    const loading = this.#loading;
    if (loading) loading.hidden = true;
    this.removeAttribute('data-open');
    delete this.dataset.mapLoaded;
  }
}

if (!customElements.get('bk-footer-map')) {
  customElements.define('bk-footer-map', BkFooterMap);
}
